import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@/components/ui/sheet'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { PurchasingStage } from '@Purchasing/Domain/Model/PurchaseProcess'
import {
    Check,
    ChevronDown,
    ChevronUp,
    Columns3,
    Eye,
    EyeOff,
    Pencil,
    Plus,
    X,
} from 'lucide-react'
import { FormEvent, ReactNode, useState } from 'react'

interface StageManagerProps {
    stages: PurchasingStage[]
    counts: Map<string, number>
    canManage: boolean
    busy: boolean
    onMove: (stage: PurchasingStage, direction: 'up' | 'down') => Promise<void>
    onRename: (stage: PurchasingStage, name: string) => Promise<void>
    onToggleActive: (stage: PurchasingStage) => Promise<void>
    onCreate: (name: string) => Promise<void>
}

const IconAction = ({
    label,
    onClick,
    disabled,
    children,
}: {
    label: string
    onClick: () => void
    disabled?: boolean
    children: ReactNode
}) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full text-muted-foreground hover:bg-accent hover:text-brand-deep"
                aria-label={label}
                disabled={disabled}
                onClick={onClick}
            >
                {children}
            </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
    </Tooltip>
)

const StageRow = ({
    stage,
    index,
    total,
    count,
    canManage,
    busy,
    onMove,
    onRename,
    onToggleActive,
}: {
    stage: PurchasingStage
    index: number
    total: number
    count: number
} & Omit<StageManagerProps, 'stages' | 'counts' | 'onCreate'>) => {
    const [editing, setEditing] = useState(false)
    const [name, setName] = useState(stage.name)
    const inactive = stage.active === false
    const isDefault = stage.key === 'new'

    const saveRename = async () => {
        const next = name.trim()
        if (next && next !== stage.name) await onRename(stage, next)
        setEditing(false)
    }

    return (
        <li
            className={cn(
                'flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2',
                inactive && 'bg-secondary opacity-70'
            )}
            aria-label={`Coluna ${index + 1}: ${stage.name}`}
        >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary font-display text-xs font-bold text-primary-foreground">
                {index + 1}
            </span>

            {editing ? (
                <form className="flex flex-1 items-center gap-1" onSubmit={(event) => { event.preventDefault(); void saveRename() }}>
                    <Input
                        autoFocus
                        aria-label={`Novo nome para ${stage.name}`}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Escape') {
                                setName(stage.name)
                                setEditing(false)
                            }
                        }}
                        className="h-8"
                    />
                    <IconAction label="Salvar nome" onClick={() => void saveRename()}>
                        <Check className="h-4 w-4" strokeWidth={1.5} />
                    </IconAction>
                    <IconAction label="Cancelar" onClick={() => { setName(stage.name); setEditing(false) }}>
                        <X className="h-4 w-4" strokeWidth={1.5} />
                    </IconAction>
                </form>
            ) : (
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground">{stage.name}</p>
                    <p className="text-xs text-muted-foreground">
                        {count} {count === 1 ? 'processo' : 'processos'}
                        {isDefault && ' · coluna de entrada'}
                        {inactive && ' · oculta no quadro'}
                    </p>
                </div>
            )}

            {canManage && !editing && (
                <div className="flex shrink-0 items-center">
                    <IconAction label="Subir (coluna mais à esquerda)" disabled={busy || index === 0} onClick={() => void onMove(stage, 'up')}>
                        <ChevronUp className="h-4 w-4" strokeWidth={1.5} />
                    </IconAction>
                    <IconAction label="Descer (coluna mais à direita)" disabled={busy || index === total - 1} onClick={() => void onMove(stage, 'down')}>
                        <ChevronDown className="h-4 w-4" strokeWidth={1.5} />
                    </IconAction>
                    <IconAction label="Renomear" disabled={busy} onClick={() => setEditing(true)}>
                        <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </IconAction>
                    <IconAction
                        label={inactive ? 'Mostrar no quadro' : isDefault ? 'A coluna de entrada não pode ser ocultada' : 'Ocultar do quadro'}
                        disabled={busy || (isDefault && !inactive)}
                        onClick={() => void onToggleActive(stage)}
                    >
                        {inactive ? <Eye className="h-4 w-4" strokeWidth={1.5} /> : <EyeOff className="h-4 w-4" strokeWidth={1.5} />}
                    </IconAction>
                </div>
            )}
        </li>
    )
}

export const StageManager = ({ stages, counts, canManage, busy, onMove, onRename, onToggleActive, onCreate }: StageManagerProps) => {
    const [newName, setNewName] = useState('')

    const submitNew = async (event: FormEvent) => {
        event.preventDefault()
        const name = newName.trim()
        if (!name) return
        await onCreate(name)
        setNewName('')
    }

    return (
        <Sheet>
            <SheetTrigger asChild>
                <Button type="button" variant="outline" className="h-9 gap-2 rounded-full border-border text-brand-deep">
                    <Columns3 className="h-4 w-4" strokeWidth={1.5} />
                    Colunas do quadro
                </Button>
            </SheetTrigger>
            <SheetContent
                className="flex w-full flex-col gap-4 overflow-y-auto sm:max-w-md"
                // o foco automático cairia no primeiro ícone e abriria o tooltip dele
                onOpenAutoFocus={(event) => event.preventDefault()}
            >
                <SheetHeader>
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-pink">Kanban · estágios</p>
                    <SheetTitle className="font-display text-xl font-normal">
                        Colunas do <span className="font-extrabold text-brand">quadro</span>
                    </SheetTitle>
                    <SheetDescription>
                        Cada estágio é uma coluna do Kanban, da esquerda para a direita na ordem abaixo.
                        Processos novos entram na primeira coluna. Ocultar uma coluna não apaga os processos dela.
                    </SheetDescription>
                </SheetHeader>

                <TooltipProvider delayDuration={200}>
                    <ol className="flex flex-col gap-2" aria-label="Estágios de Compras">
                        {stages.map((stage, index) => (
                            <StageRow
                                key={stage._id}
                                stage={stage}
                                index={index}
                                total={stages.length}
                                count={counts.get(stage._id) || 0}
                                canManage={canManage}
                                busy={busy}
                                onMove={onMove}
                                onRename={onRename}
                                onToggleActive={onToggleActive}
                            />
                        ))}
                    </ol>
                </TooltipProvider>

                {canManage ? (
                    <form className="mt-auto flex items-center gap-2 border-t border-border pt-4" onSubmit={submitNew}>
                        <Input
                            aria-label="Nome do novo estágio"
                            placeholder="Nova coluna, ex.: Aguardando arte"
                            value={newName}
                            onChange={(event) => setNewName(event.target.value)}
                        />
                        <Button type="submit" className="gap-1" disabled={busy || !newName.trim()}>
                            <Plus className="h-4 w-4" strokeWidth={1.5} />
                            Adicionar
                        </Button>
                    </form>
                ) : (
                    <p className="text-xs text-muted-foreground">Você pode ver as colunas, mas não tem permissão para alterá-las.</p>
                )}
            </SheetContent>
        </Sheet>
    )
}
